import {
  NgClass,
  NgOptimizedImage
} from '@angular/common';
import {
  Component,
  computed,
  input,
  output,
  signal
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { NgxTolgeeModule } from '@tolgee/ngx';

import moment from 'moment';
import { emulateClick } from '../../core/a11y';

export type Event = { [key: string]: any } & {
  date: string,
  start: string,
  duration: number,
}

type EventComputed = Event & { ofDaySelected: boolean, day: number, styles: string, past: boolean };

@Component({
  selector: 'app-planning',
  imports: [
    NgxTolgeeModule,
    NgClass,
    NgOptimizedImage,
    ReactiveFormsModule
  ],
  templateUrl: './planning.component.html'
})
export class PlanningComponent {
  categorySelected = signal<number>(0);
  today = signal(moment.utc().startOf('day'));
  selected = signal(this.today());
  viewing = output<{ from: string, to: string }>();
  planning = computed(() => {
    const start = this.selected().clone().startOf('week');
    const end = this.selected().clone().endOf('week');
    this.viewing.emit({ from: start.format('YYYY-MM-DD'), to: end.format('YYYY-MM-DD') });
    let cursor = start.clone();
    const days: moment.Moment[] = []
    while (cursor <= end) {
      days.push(cursor);
      cursor = moment.utc(cursor).add(1, 'day');
    }
    return days;
  });
  events = input<Event[]>([]);
  loading = input.required<boolean>();
  eventsDisplayed = computed(() => {
    const category = this.categorySelected();
    const events = this.events();
    const selected = this.selected();
    const start = selected.clone().startOf('week').startOf('day');
    const end = selected.clone().endOf('week').endOf('day');
    return events.filter(e => {
      if (category && e['course']['erpId'] !== category) {
        return false;
      }
      // Transform event start hours to URL hours
      const [ h, m ] = e.start.split(':');
      const hours = new Date().setHours(Number(h), Number(m));
      const utcHours = moment.utc(hours);
      const utcEventHours = utcHours.format("HH:mm");
      // Get now utc date and hours
      const now = moment.utc(new Date());
      const nowHour = now.format("HH:mm");
      // Get event date as UTC date
      const date = moment.utc(e.date);
      // Exclude today events with past hours
      if (utcEventHours < nowHour && date.date() === now.date() && date.month() === now.month()) {
        return false;
      }
      // Exclude events from selected period
      return date.isBetween(start, end, 'day', '[]');
    })
  })
  eventsComputed = computed(() => {
    const events = this.eventsDisplayed();
    return events.map((event: Event): EventComputed => {
      const eventComputed = event as EventComputed;
      const date = moment.utc(eventComputed.date);
      const selected = this.selected();
      eventComputed.ofDaySelected = date.isSame(selected, 'day');
      eventComputed.day = date.weekday() + 1;
      eventComputed.styles = this.styles(event);
      eventComputed.past = date.isBefore(this.today());
      return eventComputed;
    })
  })
  timeSlots = computed(() => {
    return this.everyFifteenMinutes();
  });
  categories = computed(() => this.events()
    .map(e => e['course'])
    .filter((e1, idx, arr) => arr.findIndex(e2 => e1.erpId === e2.erpId) === idx));
  eventClicked = output<Event>()
  protected readonly emulateClick = emulateClick;

  styles(event: Event): string {
    const [ hour, minute ] = event.start.split(':').map(Number);
    const totalHoursToAdd = (hour + minute / 60 - 10)
    const startRounded = Math.round(totalHoursToAdd * 16).toFixed(2);
    const start = Math.floor(Number(startRounded) + 5);
    const duration = event.duration / 15 * 4;
    return ` grid-row: ${start} / span ${duration}`;
  }

  previousWeek(): void {
    this.selected.update(moment => moment.clone().subtract(1, 'week'))
  }

  nextWeek(): void {
    this.selected.update(moment => moment.clone().add(1, 'week'))
  }

  /**
   * Create a list of hours each 15 minutes
   * @returns The list of hours
   */
  everyFifteenMinutes(): string[] {
    const result = [];
    result.push('');
    for (let hours = 10; hours < 22; hours++) {
      for (let minutes = 0; minutes < 60; minutes = minutes + 15) {
        let h = '';
        let m = '';
        if (hours < 10) {
          h = '0' + hours;
        } else {
          h = String(hours);
        }
        if (minutes < 10) {
          m = '0' + minutes;
        } else {
          m = String(minutes);
        }
        result.push(h + ':' + m);
      }
    }
    result.push('22:00');
    return result;
  }

  changeCategory($event: any): void {
    this.categorySelected.set(Number(($event['target'] as HTMLInputElement).value))
  }
}
